import dayjs from "dayjs";
import {
  DEFAULT_CLIENT_ON_COST_PERCENTS,
  applyHolidayPayToRate,
  applyShiftTypeUplift,
  calculateClientHourlyFromWorkerRate,
} from "./roleRateCardPricing";

export { DEFAULT_CLIENT_ON_COST_PERCENTS };

const UK_BANK_HOLIDAYS = [
  "2024-01-01",
  "2024-03-29",
  "2024-04-01",
  "2024-05-06",
  "2024-05-27",
  "2024-08-26",
  "2024-12-25",
  "2024-12-26",
  "2025-01-01",
  "2025-04-18",
  "2025-04-21",
  "2025-05-05",
  "2025-05-26",
  "2025-08-25",
  "2025-12-25",
  "2025-12-26",
  "2026-01-01",
  "2026-04-03",
  "2026-04-06",
  "2026-05-04",
  "2026-05-25",
  "2026-08-31",
  "2026-12-25",
  "2026-12-28",
];

const parseHHMM = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  const twelveHourMatch = normalized.match(
    /^(\d{1,2})(?::(\d{1,2}))?\s*(am|pm)$/
  );

  if (twelveHourMatch) {
    const rawHour = Number(twelveHourMatch[1]);
    const minute = Number(twelveHourMatch[2] || 0);
    if (rawHour < 1 || rawHour > 12 || minute > 59) {
      return { h: Number.NaN, m: Number.NaN };
    }
    const hour =
      (rawHour % 12) + (twelveHourMatch[3] === "pm" ? 12 : 0);
    return { h: hour, m: minute };
  }

  const twentyFourHourMatch = normalized.match(/^(\d{1,2})(?::(\d{1,2}))?$/);
  if (!twentyFourHourMatch) {
    return { h: Number.NaN, m: Number.NaN };
  }

  const hour = Number(twentyFourHourMatch[1]);
  const minute = Number(twentyFourHourMatch[2] || 0);
  if (hour > 23 || minute > 59) {
    return { h: Number.NaN, m: Number.NaN };
  }
  return { h: hour, m: minute };
};

const isBankHoliday = (dateStr) => UK_BANK_HOLIDAYS.includes(dateStr);
const isWeekend = (dateStr) => {
  const day = dayjs(dateStr).day();
  return day === 0 || day === 6;
};

const getDayNightSegmentsFromNightTimings = (nightTimingsStr) => {
  const fallback = [
    { start: 0, end: 6 * 60, type: "night" },
    { start: 6 * 60, end: 22 * 60, type: "day" },
    { start: 22 * 60, end: 24 * 60, type: "night" },
  ];
  if (!nightTimingsStr || typeof nightTimingsStr !== "string") return fallback;

  const [startRaw, endRaw] = nightTimingsStr.split("-");
  if (!startRaw || !endRaw) return fallback;

  const s = parseHHMM(startRaw.trim());
  const e = parseHHMM(endRaw.trim());
  const nightStart = s.h * 60 + s.m;
  const nightEnd = e.h * 60 + e.m;

  if (Number.isNaN(nightStart) || Number.isNaN(nightEnd)) return fallback;

  if (nightStart === nightEnd) {
    return [{ start: 0, end: 24 * 60, type: "night" }];
  }

  if (nightStart < nightEnd) {
    return [
      { start: 0, end: nightStart, type: "day" },
      { start: nightStart, end: nightEnd, type: "night" },
      { start: nightEnd, end: 24 * 60, type: "day" },
    ].filter((s) => s.end > s.start);
  }

  return [
    { start: 0, end: nightEnd, type: "night" },
    { start: nightEnd, end: nightStart, type: "day" },
    { start: nightStart, end: 24 * 60, type: "night" },
  ].filter((s) => s.end > s.start);
};

export const calculateShiftDuration = (startTime, endTime) => {
  const start = parseHHMM(startTime);
  const end = parseHHMM(endTime);

  const startMinutes = start.h * 60 + start.m;
  let endMinutes = end.h * 60 + end.m;
  if (endMinutes <= startMinutes) endMinutes += 24 * 60;

  return (endMinutes - startMinutes) / 60;
};

export const calculateDayNightHours = (startTime, endTime, nightTimingsStr = "") => {
  const start = parseHHMM(startTime);
  const end = parseHHMM(endTime);

  const startMinutes = start.h * 60 + start.m;
  let endMinutes = end.h * 60 + end.m;
  if (endMinutes <= startMinutes) endMinutes += 24 * 60;

  const segments = getDayNightSegmentsFromNightTimings(nightTimingsStr);
  let dayMinutes = 0;
  let nightMinutes = 0;

  for (const seg of segments) {
    const overlapStart = Math.max(startMinutes, seg.start);
    const overlapEnd = Math.min(Math.min(endMinutes, 24 * 60), seg.end);
    const overlap = Math.max(0, overlapEnd - overlapStart);
    if (seg.type === "day") dayMinutes += overlap;
    else nightMinutes += overlap;
  }

  if (endMinutes > 24 * 60) {
    const secondDayEnd = endMinutes - 24 * 60;
    for (const seg of segments) {
      const overlapStart = Math.max(0, seg.start);
      const overlapEnd = Math.min(secondDayEnd, seg.end);
      const overlap = Math.max(0, overlapEnd - overlapStart);
      if (seg.type === "day") dayMinutes += overlap;
      else nightMinutes += overlap;
    }
  }

  return { dayHours: dayMinutes / 60, nightHours: nightMinutes / 60 };
};

/**
 * Resolve which uplift(s) apply for a concrete shift date/time.
 * Selection rules unchanged; money math uses Standard Worker Rate (Basic + Holiday).
 *
 * HolidayPay = Basic × Holiday%
 * Standard = Basic + HolidayPay
 * Worker Rate = Standard + Σ(Standard × ApplicableUplift%)
 */
export const resolveWorkerHourlyWithUplift = ({
  dateStr,
  startTime,
  endTime,
  isUrgent,
  workerBaseRate,
  uplifts,
  nightTimingsStr,
}) => {
  const u = uplifts || {};
  const holiday = applyHolidayPayToRate(workerBaseRate, u);
  const standard = holiday.rateWithHoliday;

  const isCurrentDateBankHoliday = isBankHoliday(dateStr);
  const start = parseHHMM(startTime);
  const end = parseHHMM(endTime);
  const isOvernightShift = end.h * 60 + end.m <= start.h * 60 + start.m;

  let isNextDateBankHoliday = false;
  if (isOvernightShift) {
    const nextDate = dayjs(dateStr).add(1, "day").format("YYYY-MM-DD");
    isNextDateBankHoliday = isBankHoliday(nextDate);
  }

  const bankHolidayApplies = isCurrentDateBankHoliday || isNextDateBankHoliday;
  const urgentApplies = !!isUrgent;

  const applied = [];
  if (bankHolidayApplies && urgentApplies) {
    applied.push(
      { key: "bankHoliday", percent: Number(u.bankHolidayUplift) || 0 },
      { key: "urgent", percent: Number(u.urgentUplift) || 0 }
    );
  } else if (bankHolidayApplies) {
    applied.push({
      key: "bankHoliday",
      percent: Number(u.bankHolidayUplift) || 0,
    });
  } else if (urgentApplies) {
    applied.push({ key: "urgent", percent: Number(u.urgentUplift) || 0 });
  } else {
    const { dayHours, nightHours } = calculateDayNightHours(
      startTime,
      endTime,
      nightTimingsStr
    );
    if (nightHours >= dayHours) {
      applied.push({ key: "night", percent: Number(u.nightUplift) || 0 });
    } else if (isWeekend(dateStr)) {
      applied.push({ key: "weekend", percent: Number(u.weekendUplift) || 0 });
    }
  }

  let upliftAmount = 0;
  const upliftAmounts = {
    weekend: 0,
    night: 0,
    urgent: 0,
    bankHoliday: 0,
  };
  for (const item of applied) {
    const amount = applyShiftTypeUplift(standard, item.percent).upliftAmount;
    upliftAmount += amount;
    if (item.key in upliftAmounts) upliftAmounts[item.key] += amount;
  }

  return {
    workerBaseRate: holiday.baseRate,
    holidayPayPct: holiday.holidayPayPct,
    holidayPayAmount: holiday.holidayPayAmount,
    rateWithHoliday: standard,
    workerRate: standard + upliftAmount,
    upliftAmount,
    upliftAmounts,
  };
};

export const calculateHourlyPrice = (args) =>
  resolveWorkerHourlyWithUplift(args).workerRate;

/**
 * Client Rate = Final Worker Rate + NI + Pension + Levy + Margin
 * (all on-costs and margin from final Worker Rate; holiday already in worker rate).
 */
export const calculateClientHourlyRate = ({
  workerBaseRate,
  workerRate,
  uplifts = {},
  marginPercentage = 0,
  holidayPayPct,
  holidayPayAmount,
}) => {
  const base = parseFloat(workerBaseRate) || 0;
  const rate = Number(workerRate);
  const workerHourly = Number.isFinite(rate) ? rate : base;

  let holidayPct = holidayPayPct;
  let holidayAmount = holidayPayAmount;
  if (holidayPct == null || holidayAmount == null) {
    const holiday = applyHolidayPayToRate(base, uplifts);
    holidayPct = holiday.holidayPayPct;
    holidayAmount = holiday.holidayPayAmount;
  }

  return calculateClientHourlyFromWorkerRate({
    workerRate: workerHourly,
    uplifts,
    marginPercentage,
    workerBaseRate: base,
    holidayPayPct: holidayPct,
    holidayPayAmount: holidayAmount,
  });
};

const emptyPricingBreakdown = () => ({
  workerBaseAmount: 0,
  weekendUpliftAmount: 0,
  nightUpliftAmount: 0,
  urgentUpliftAmount: 0,
  bankHolidayUpliftAmount: 0,
  upliftAmount: 0,
  holidayPayAmount: 0,
  niAmount: 0,
  pensionAmount: 0,
  levyAmount: 0,
  marginAmount: 0,
  total: 0,
});

/**
 * Full booking estimate with £ component totals (for pricing UI breakdown).
 */
export const calculatePricingBreakdownFromPatterns = ({
  shiftPattern = [],
  sectors = [],
  isUrgent = false,
  uplifts = {},
  generateDatesArray,
}) => {
  const breakdown = emptyPricingBreakdown();
  if (!Array.isArray(shiftPattern) || !Array.isArray(sectors) || !uplifts) {
    return breakdown;
  }

  shiftPattern.forEach((pattern) => {
    const sector = sectors.find((s) => s.slug === pattern.sectorSlug);
    const role = sector?.roles?.find((r) => r.slug === pattern.roleSlug);
    const workerBaseRate = role?.workerBaseRate ?? "0.00";
    const marginPercentage = role?.marginPercentage ?? 0;
    const nightTimings = sector?.nightTimings ?? "";
    const quantity = pattern.quantity || 1;

    const shiftDurationHours = calculateShiftDuration(
      pattern.startTime,
      pattern.endTime
    );
    const dates = generateDatesArray(
      pattern.startDate,
      pattern.endDate,
      pattern.excludeWeekend,
      pattern.excludeSpecificDates
    );
    const multiplier = shiftDurationHours * quantity;

    dates.forEach((dateStr) => {
      const worker = resolveWorkerHourlyWithUplift({
        dateStr,
        startTime: pattern.startTime,
        endTime: pattern.endTime,
        isUrgent,
        workerBaseRate,
        uplifts,
        nightTimingsStr: nightTimings,
      });

      const client = calculateClientHourlyRate({
        workerBaseRate,
        workerRate: worker.workerRate,
        uplifts,
        marginPercentage,
        holidayPayPct: worker.holidayPayPct,
        holidayPayAmount: worker.holidayPayAmount,
      });

      breakdown.workerBaseAmount += worker.workerBaseRate * multiplier;
      breakdown.weekendUpliftAmount +=
        worker.upliftAmounts.weekend * multiplier;
      breakdown.nightUpliftAmount += worker.upliftAmounts.night * multiplier;
      breakdown.urgentUpliftAmount += worker.upliftAmounts.urgent * multiplier;
      breakdown.bankHolidayUpliftAmount +=
        worker.upliftAmounts.bankHoliday * multiplier;
      breakdown.upliftAmount += worker.upliftAmount * multiplier;
      breakdown.holidayPayAmount +=
        (worker.holidayPayAmount || client.holidayPayAmount || 0) * multiplier;
      breakdown.niAmount += client.niAmount * multiplier;
      breakdown.pensionAmount += client.pensionAmount * multiplier;
      breakdown.levyAmount += client.levyAmount * multiplier;
      breakdown.marginAmount += client.marginAmount * multiplier;
      breakdown.total += client.clientHourly * multiplier;
    });
  });

  return breakdown;
};

export const calculateTotalPriceFromPatterns = (args) =>
  calculatePricingBreakdownFromPatterns(args).total;

export const calculateDiscountPricingDetails = ({
  shiftPattern = [],
  totalPrice = 0,
  discounts = [],
  generateDatesArray,
}) => {
  let totalWorkers = 0;
  let totalWorkerShifts = 0;

  shiftPattern.forEach((pattern) => {
    totalWorkers += pattern.quantity || 0;
    const dates = generateDatesArray(
      pattern.startDate,
      pattern.endDate,
      pattern.excludeWeekend,
      pattern.excludeSpecificDates
    );
    totalWorkerShifts += (pattern.quantity || 0) * dates.length;
  });

  let selectedDiscount = null;
  let discountPercentage = 0;
  let discountType = null;
  if (discounts.length > 0 && totalWorkers > 0) {
    const sortedDiscounts = [...discounts]
      .filter((d) => d.isActive)
      .sort((a, b) => b.numberOfStaff - a.numberOfStaff);

    if (totalWorkers >= 10 && totalWorkers <= 13) {
      selectedDiscount = sortedDiscounts.find((d) => d.numberOfStaff === 12);
    } else if (totalWorkers >= 14) {
      selectedDiscount =
        sortedDiscounts.find((d) => d.numberOfStaff === 14) ||
        sortedDiscounts.find((d) => d.numberOfStaff >= 14);
    }

    if (selectedDiscount) {
      discountPercentage = parseFloat(selectedDiscount.discountValue || 0);
      discountType = selectedDiscount.discountType || "percentage";
    }
  }

  let discountAmount = 0;
  if (discountType === "percentage") discountAmount = (totalPrice * discountPercentage) / 100;
  else if (discountType === "fixed") discountAmount = discountPercentage;

  return {
    totalWorkers,
    totalWorkerShifts,
    discountPercentage,
    discountType,
    discountAmount,
    originalPrice: totalPrice,
    finalPrice: totalPrice - discountAmount,
    selectedDiscount,
  };
};
